<template>
  <details v-if="model.enabled !== false" class="repair-market">
    <summary>
      <span class="repair-eyebrow">大盘修复</span>
      <strong>{{ summary }}</strong>
      <span class="repair-market-date">{{ model.breadth && model.breadth.trade_date || '待入库' }} · 日线确认</span>
      <span class="repair-expand">展开指标 ↗</span>
    </summary>
    <div class="repair-market-body">
      <div class="repair-index-grid">
        <article v-for="index in indices" :key="index.code">
          <header><strong>{{ index.name }}</strong><small>{{ index.latest.trade_date || '暂无日线' }}</small></header>
          <p :class="{ 'repair-positive': index.latest.actionable }">{{ indexLabel(index) }} <b>{{ count(index.latest.evidence_count) }}/4</b></p>
          <dl>
            <div><dt>地量锚点</dt><dd>{{ index.latest.anchor_date || '--' }}</dd></div>
            <div><dt>锚定参考线</dt><dd>{{ value(index.latest.avwap) }} <small>{{ value(index.latest.avwap_gap_pct, '%') }}</small></dd></div>
            <div><dt>0.5扇线</dt><dd>{{ value(index.latest.fan_05) }}</dd></div>
            <div><dt>相对前5日均量</dt><dd>{{ value(index.latest.volume_multiple_5, '倍') }}</dd></div>
            <div><dt>历史存储 / 指标连续</dt><dd>{{ count(index.stored_history_count) }} / {{ count(index.latest.history_segment_count) }} 根</dd></div>
          </dl>
          <small v-if="index.freshness !== 'ok'" class="repair-warning">{{ index.freshness === 'stale' ? '日线过期，仅供回看' : '请补齐指数日线与交易日历' }}</small>
          <small v-if="index.issues && index.issues.length" class="repair-warning">{{ index.issues.join('；') }}</small>
        </article>
      </div>
      <section class="repair-breadth" aria-label="市场修复广度">
        <header><strong>修复广度</strong><small>{{ breadth.status === 'ok' ? '完整日线样本' : '样本不足或数据过期' }} · 覆盖 {{ value(breadth.coverage_pct, '%') }}（{{ breadth.sample_count || 0 }}/{{ breadth.universe_count || 0 }}）</small></header>
        <dl>
          <div><dt>上涨家数占比</dt><dd>{{ value(breadth.up_pct, '%') }}</dd></div>
          <div><dt>站上MA5 / MA10</dt><dd>{{ value(breadth.above_ma5_pct, '%') }} / {{ value(breadth.above_ma10_pct, '%') }}</dd><small>有效样本 {{ breadth.ma5_sample_count || 0 }} / {{ breadth.ma10_sample_count || 0 }}</small></div>
          <div><dt>20日新低家数 · 前日 → 当日</dt><dd>{{ houses(breadth.previous_new_low20_count) }} → {{ houses(breadth.new_low20_count) }}</dd><small>触及近20日最低价 · 有效样本 {{ houses(breadth.new_low20_sample_count) }}</small></div>
          <div><dt>跌幅≥9.5% · 前日 → 当日</dt><dd>{{ houses(breadth.previous_large_fall_count) }} → {{ houses(breadth.large_fall_count) }}</dd><small>大跌统计，非跌停家数</small></div>
          <div>
            <dt>成交额（有效样本）</dt><dd>{{ money(breadth.amount_yuan) }}</dd>
            <small>{{ breadth.amount_trade_date || '待入库' }} · 盘后日线 · Tushare</small>
            <small>覆盖 {{ value(breadth.amount_coverage_pct, '%') }}（{{ houses(breadth.amount_sample_count) }} / {{ houses(breadth.amount_universe_count) }}）</small>
            <small v-if="amountWarning" class="repair-warning">{{ amountWarning }}</small>
          </div>
          <div>
            <dt>成交额倍数 / 60日分位</dt><dd>{{ value(breadth.amount_multiple_5, '倍') }} / {{ value(breadth.amount_percentile_60, '%') }}</dd>
            <small>倍数相对前5日均额 · 已核验 {{ breadth.amount_history_count || 0 }}/60日</small>
            <small v-for="issue in amountMetricIssues" :key="issue" class="repair-warning">{{ issue }}</small>
          </div>
        </dl>
      </section>
      <p class="repair-market-note">{{ model.note || '等待已完成日线入库；刷新页面不触发联网补采。' }} 不改变现有市场风险闸门。</p>
    </div>
  </details>
</template>

<script>
export default {
  name: 'MarketRepairPanel',
  props: { model: { type: Object, default: () => ({}) } },
  computed: {
    indices () { return (this.model.indices || []).map(index => ({ ...index, latest: index.latest || {} })) },
    breadth () { return this.model.breadth || {} },
    amountWarning () {
      return (this.breadth.amount_issues || []).join('；') || (this.breadth.amount_status ? '' : '等待成交额历史回补')
    },
    amountMetricIssues () {
      const issues = []
      if (this.breadth.amount_multiple_5 == null && (this.breadth.amount_multiple_5_issues || []).length) issues.push(`5日倍数：${this.breadth.amount_multiple_5_issues.join('；')}`)
      if (this.breadth.amount_percentile_60 == null && (this.breadth.amount_percentile_60_issues || []).length) issues.push(`60日分位：${this.breadth.amount_percentile_60_issues.join('；')}`)
      return issues
    },
    summary () {
      const ready = this.indices.filter(index => index.freshness === 'ok' && index.latest.available)
      if (!ready.length) return '等待完整日线'
      const repaired = ready.filter(index => index.latest.actionable).length
      const amountPending = this.breadth.amount_status && (this.breadth.amount_status !== 'ok' || !this.breadth.amount_comparable)
      return `${repaired ? `${repaired}项指数结构修复` : '观察筑底证据'}${ready.length < this.indices.length || this.breadth.status !== 'ok' || amountPending ? ' · 数据待补齐' : ''}`
    }
  },
  methods: {
    value (value, unit = '') { return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) ? `${Number(value).toFixed(2)}${unit}` : '--' },
    count (value) { return value !== null && value !== undefined && Number.isFinite(Number(value)) ? String(value) : '--' },
    houses (value) { return value === null || value === undefined || value === '' || !Number.isFinite(Number(value)) ? '--' : `${Number(value)}家` },
    money (value) {
      if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return '--'
      const number = Number(value)
      return number >= 1e12 ? `${(number / 1e12).toFixed(2)}万亿元` : number >= 1e8 ? `${(number / 1e8).toFixed(2)}亿元` : number >= 1e4 ? `${(number / 1e4).toFixed(2)}万元` : `${number.toFixed(2)}元`
    },
    indexLabel (index) {
      const stage = index.latest.stage
      return stage === 'confirmed' ? '结构修复确认' : stage === 'retested' ? '锚定线回踩确认' : (index.latest.stage_label || '数据不足')
    }
  }
}
</script>

<style scoped>
.repair-market { margin: -10px 0 22px; background: var(--paper); border: 1px solid var(--hairline); border-radius: 8px; color: var(--ink); }
.repair-market summary { display: flex; gap: 14px; align-items: center; padding: 12px 16px; cursor: pointer; list-style: none; font-size: 12px; }
.repair-market summary::-webkit-details-marker { display: none; }
.repair-eyebrow { color: var(--coral); flex-shrink: 0; font-size: 11px; }
.repair-market-date, .repair-expand { color: var(--muted); font-size: 11px; }
.repair-expand { margin-left: auto; flex-shrink: 0; }
.repair-market-body { padding: 0 16px 14px; }
.repair-index-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 12px; }
.repair-index-grid article { padding: 14px; border: 1px solid var(--hairline); border-radius: 6px; }
.repair-market header { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; font-size: 12px; flex-wrap: wrap; }
.repair-market small, .repair-market dt { color: var(--muted); font-size: 10px; line-height: 1.6; }
.repair-index-grid p { font-size: 12px; display: flex; justify-content: space-between; }
.repair-positive { color: #1d8b63; }
.repair-market dl { margin: 0; }
.repair-index-grid dl > div { display: flex; justify-content: space-between; gap: 8px; margin-top: 9px; }
.repair-market dd { margin: 0; font-size: 12px; font-variant-numeric: tabular-nums; }
.repair-breadth { margin-top: 18px; }
.repair-breadth dl { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 16px; margin-top: 14px; }
.repair-breadth dd { margin-top: 5px; }
.repair-breadth dl > div { min-width: 0; overflow-wrap: anywhere; }
.repair-breadth dl small { display: block; }
.repair-market .repair-warning { display: block; margin-top: 8px; color: #a36914; }
.repair-market-note { margin: 16px 0 0; color: var(--muted); font-size: 10px; line-height: 1.7; }
@media (max-width: 760px) {
  .repair-market { margin-top: -6px; margin-bottom: 16px; }
  .repair-market summary { flex-wrap: wrap; gap: 8px 12px; min-height: 44px; padding: 12px; }
  .repair-market-date { order: 4; flex-basis: 100%; }
  .repair-market-body { padding: 0 12px 12px; }
  .repair-index-grid { grid-template-columns: 1fr; }
  .repair-breadth dl { grid-template-columns: repeat(2,minmax(0,1fr)); }
}
</style>
